// HighlightDonutChart.tsx
import React, { useRef, useEffect } from "react";
import { Chart, ChartConfiguration, TooltipItem } from "chart.js";
import { DoughnutController, ArcElement, Tooltip, Legend } from "chart.js";
import styled from "styled-components";

// Chart.js 에 필요한 컨트롤러/요소 등록
Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const Container = styled.div`
  position: relative;
  width: 240px;
  height: 240px;
  padding: 10px;
`;

interface HighlightDonutChartProps {
  labels: string[]; // ['감정', '음량', '속도', '톤', '변조']
  values: number[]; // [8, 4, 2, 6, 6]
  colors?: string[]; // 각 세그먼트 색상, 없으면 기본값 사용
}

const HighlightDonutChart: React.FC<HighlightDonutChartProps> = ({
  labels,
  values,
  colors = ["#467FF2", "#1B2C4F", "#2F4D9C", "#6A7BEF", "#A6A9F1"],
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // 기존 차트 파괴
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    // 총합 계산
    const total = values.reduce((sum, v) => sum + v, 0);

    const config: ChartConfiguration<"doughnut", number[], string> = {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderWidth: 0,
          },
        ],
      },
      options: {
        cutout: "80%", // 중앙 구멍 크기
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: function (context: TooltipItem<"doughnut">) {
                const value = context.parsed as number;
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return `\u00A0${pct}%`;
              },
            },
          },
        },
      },
    };

    const ctx = canvasRef.current.getContext("2d")!;
    chartRef.current = new Chart(ctx, config);

    return () => {
      chartRef.current?.destroy();
    };
  }, [labels, values, colors]);

  return (
    <Container>
      <canvas ref={canvasRef} />
    </Container>
  );
};

export default HighlightDonutChart;
